"use client";

import { Calendar, ChevronLeft, User } from "lucide-react";

import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="space-y-6">
          <div className="h-6 w-32 animate-pulse rounded-md bg-muted" />
          <div className="h-12 w-3/4 animate-pulse rounded-md bg-muted" />
          <div className="h-4 w-1/2 animate-pulse rounded-md bg-muted" />
          <div className="aspect-video w-full animate-pulse rounded-lg bg-muted" />
          <div className="space-y-3">
            <div className="h-4 w-full animate-pulse rounded-md bg-muted" />
            <div className="h-4 w-full animate-pulse rounded-md bg-muted" />
            <div className="h-4 w-5/6 animate-pulse rounded-md bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-24 text-center sm:px-6 lg:px-8">
        <p className="text-muted-foreground">
          {"We couldn't load this resource. Please try again later."}
        </p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-24 text-center sm:px-6 lg:px-8">
        <h2 className="font-default text-2xl font-semibold text-foreground">
          Resource not found
        </h2>
        <p className="mt-3 text-muted-foreground">
          {"The information you're looking for doesn't exist or has been moved."}
        </p>
        <Link to="/HomePage" className="mt-6 inline-block">
          <Button variant="outline">
            <ChevronLeft className="size-4" />
            Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorInitials = post.author?.name
    ? post.author.name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "WS";

  const authorAvatar =
    post.author?.avatar &&
    (post.author.avatar["96"] ||
      post.author.avatar["48"] ||
      post.author.avatar["24"]);

  return (
    <article className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
      {}
      <div className="mb-8">
        <Link
          to="/GuidancePage"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="size-4" />
          Back to all guidance
        </Link>
      </div>

      {}
      {post.categories && post.categories.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-2">
          {post.categories.map((cat, i) => (
            <Link
              key={cat.id}
              to={cat.link || "#"}
              data-index={i}
              className="inline-flex items-center bg-primary px-3 py-1 text-xs font-medium uppercase tracking-wider text-primary-foreground transition-opacity hover:opacity-90"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      )}

      {}
      <h1
        className="font-default text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-6xl"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      {}
      {post.excerpt && (
        <div
          className="mt-6 text-lg leading-relaxed text-muted-foreground sm:text-xl"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {}
      <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
        {post.author && (
          <div className="flex items-center gap-3">
            <Avatar className="size-9">
              {authorAvatar && (
                <AvatarImage src={authorAvatar} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-secondary text-xs font-medium text-secondary-foreground">
                {authorInitials}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                Author
              </span>
              <span className="font-medium text-foreground">
                {post.author.name}
              </span>
            </div>
          </div>
        )}

        {formattedDate && (
          <>
            <Separator
              orientation="vertical"
              className="hidden h-9 sm:block"
            />
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-wider">
                  Published
                </span>
                <span className="font-medium text-foreground">
                  {formattedDate}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      <Separator className="my-10" />

      {}
      {post.featuredImage && (
        <figure className="mb-10 overflow-hidden rounded-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="h-auto w-full object-cover"
          />
        </figure>
      )}

      {}
      {post.content && (
        <div
          className="prose prose-lg max-w-none font-default text-base leading-relaxed text-foreground [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:opacity-80 [&_blockquote]:my-6 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-sm [&_code]:font-mono [&_h2]:mb-4 [&_h2]:mt-10 [&_h2]:font-default [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h3]:mb-3 [&_h3]:mt-8 [&_h3]:font-default [&_h3]:text-2xl [&_h3]:font-semibold [&_h4]:mb-2 [&_h4]:mt-6 [&_h4]:font-default [&_h4]:text-xl [&_h4]:font-semibold [&_img]:my-6 [&_img]:rounded-lg [&_li]:my-2 [&_ol]:my-5 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-5 [&_pre]:my-6 [&_pre]:overflow-x-auto [&_pre]:bg-muted [&_pre]:p-4 [&_strong]:font-semibold [&_strong]:text-foreground [&_ul]:my-5 [&_ul]:list-disc [&_ul]:pl-6"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <>
          <Separator className="my-10" />
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Tagged
            </span>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, i) => (
                <Link
                  key={tag.id}
                  to={tag.link || "#"}
                  data-index={i}
                  className="inline-flex items-center border border-border bg-background px-3 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent"
                >
                  {tag.name}
                </Link>
              ))}
            </div>
          </div>
        </>
      )}

      {}
      {post.author && (
        <>
          <Separator className="my-10" />
          <Card className="border border-border bg-secondary/50">
            <CardContent className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
              <Avatar className="size-16 shrink-0">
                {authorAvatar && (
                  <AvatarImage src={authorAvatar} alt={post.author.name} />
                )}
                <AvatarFallback className="bg-primary text-base font-semibold text-primary-foreground">
                  {authorInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                  <User className="size-3.5" />
                  Written by
                </div>
                <h3 className="mt-1 font-default text-xl font-semibold text-foreground">
                  {post.author.name}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Expert contributor at Water Service Provider Guide, providing essential resources for community members, committee members, and operational staff.
                </p>
              </div>
              {post.author.link && (
                <Link to={post.author.link} className="shrink-0">
                  <Button variant="outline" size="sm">
                    View profile
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </article>
  );
}

export default function Article() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="10761"
      id="single-post-template-section"
      className="bg-background text-foreground"
    >
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}